import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, ArrowLeft, User, AlertTriangle, TrendingUp, 
  TrendingDown, Target, Clock, MessageCircle, FileText,
  Lightbulb, CheckCircle, XCircle, Star, Award, Activity
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentTrackingPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get("id");
  
  const [tracking, setTracking] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReco, setLoadingReco] = useState(false);

  useEffect(() => {
    if (studentId) {
      fetchTracking();
    }
  }, [studentId]);

  const fetchTracking = async () => {
    try {
      const response = await axios.get(`${API}/tracking/student/${studentId}`);
      setTracking(response.data);
    } catch (error) {
      console.error("Error fetching tracking:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingReco(true);
    try {
      const response = await axios.get(`${API}/tracking/student/${studentId}/recommendations`);
      setRecommendations(response.data);
      toast.success("Recommandations générées !");
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setLoadingReco(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "critique": return "bg-[#EF4444]";
      case "élevé": return "bg-[#F97316]";
      case "modéré": return "bg-[#EAB308]";
      default: return "bg-[#22C55E]";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "maîtrisée": return <CheckCircle className="w-4 h-4 text-[#22C55E]" />;
      case "en_cours": return <Activity className="w-4 h-4 text-[#EAB308]" />;
      case "en_difficulté": return <XCircle className="w-4 h-4 text-[#EF4444]" />;
      default: return <Target className="w-4 h-4 text-[#64748B]" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "maîtrisée": return "Maîtrisée";
      case "en_cours": return "En cours";
      case "en_difficulté": return "En difficulté";
      default: return "Non abordée";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement du suivi...</p>
        </div>
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <p className="text-[#64748B]">Élève non trouvé</p>
      </div>
    );
  }

  const student = tracking.student;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="student-tracking-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/director")}
            className="text-[#1A365D]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Suivi Pédagogique</span>
        </div>
      </nav>

      <main className="px-4 py-6 max-w-6xl mx-auto page-enter">
        {/* Student Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              {student.first_name} {student.last_name}
            </h1>
            <p className="text-[#64748B]">{student.class_name} • Niveau {student.level}</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge className={`${getRiskColor(tracking.risk_level)} text-white px-4 py-2`}>
              Risque {tracking.risk_level}
            </Badge>
            <div className="flex items-center gap-2 bg-[#D4AF37]/10 px-4 py-2 rounded-lg">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-bold text-[#1A365D]">{student.points} pts</span>
            </div>
          </div>
        </div>

        {/* Risk Factors Alert */}
        {tracking.risk_factors.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-[#EF4444]" data-testid="risk-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#0F172A] mb-2">Facteurs de risque identifiés</p>
                  <ul className="space-y-1">
                    {tracking.risk_factors.map((factor, idx) => (
                      <li key={idx} className="text-sm text-[#64748B]">• {factor}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="card-sjh">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-[#1A365D] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#1A365D]">{tracking.engagement.total_exercises}</p>
              <p className="text-xs text-[#64748B]">Exercices</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-[#22C55E] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#1A365D]">{tracking.engagement.average_score}/20</p>
              <p className="text-xs text-[#64748B]">Moyenne</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4 text-center">
              <MessageCircle className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#1A365D]">{tracking.engagement.total_group_messages}</p>
              <p className="text-xs text-[#64748B]">Messages Agora</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-[#1A365D] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#1A365D]">{student.diagnostic_score || "-"}%</p>
              <p className="text-xs text-[#64748B]">Diagnostic</p>
            </CardContent>
          </Card>
          <Card className="card-sjh">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-[#64748B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#1A365D]">{tracking.engagement.days_active_last_month}</p>
              <p className="text-xs text-[#64748B]">Jours actifs/mois</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Strengths & Weaknesses */}
          <Card className="card-sjh">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Forces & Faiblesses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-[#22C55E] flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4" /> Points forts
                  </p>
                  {tracking.strengths.length > 0 ? (
                    <ul className="space-y-2">
                      {tracking.strengths.map((s, idx) => (
                        <li key={idx} className="flex items-center justify-between bg-[#22C55E]/10 px-3 py-2 rounded">
                          <span className="text-sm text-[#0F172A]">{s.notion}</span>
                          <Badge className="bg-[#22C55E]">{s.average}/20</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#64748B] italic">Aucun point fort identifié</p>
                  )}
                </div>
                
                <div>
                  <p className="text-sm font-medium text-[#EF4444] flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4" /> Points à travailler
                  </p>
                  {tracking.weaknesses.length > 0 ? (
                    <ul className="space-y-2">
                      {tracking.weaknesses.map((w, idx) => (
                        <li key={idx} className="flex items-center justify-between bg-[#EF4444]/10 px-3 py-2 rounded">
                          <span className="text-sm text-[#0F172A]">{w.notion}</span>
                          <Badge className="bg-[#EF4444]">{w.average}/20</Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#64748B] italic">Aucune faiblesse identifiée</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="card-sjh">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Lightbulb className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
                  Recommandations IA
                </CardTitle>
                <Button
                  size="sm"
                  onClick={fetchRecommendations}
                  disabled={loadingReco}
                  className="bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                >
                  {loadingReco ? "Génération..." : "Générer"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recommendations ? (
                <ul className="space-y-3">
                  {recommendations.recommendations.map((r, idx) => (
                    <li key={idx} className="p-3 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={
                          r.priority === "haute" ? "bg-[#EF4444]" :
                          r.priority === "moyenne" ? "bg-[#EAB308]" : "bg-[#64748B]"
                        }>
                          {r.priority}
                        </Badge>
                        <Badge variant="outline">{r.category}</Badge>
                      </div>
                      <p className="font-medium text-[#0F172A] text-sm">{r.action}</p>
                      <p className="text-xs text-[#64748B] mt-1">{r.details}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-[#64748B]">
                  <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Cliquez sur "Générer" pour obtenir des recommandations personnalisées</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Notion Progress */}
        <Card className="card-sjh mb-8">
          <CardHeader>
            <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Progression par notion (17 notions du programme)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(tracking.notion_progress).map(([notion, data]) => (
                <div 
                  key={notion}
                  className={`p-3 rounded-lg border ${
                    data.status === "maîtrisée" ? "bg-[#22C55E]/10 border-[#22C55E]/30" :
                    data.status === "en_difficulté" ? "bg-[#EF4444]/10 border-[#EF4444]/30" :
                    data.status === "en_cours" ? "bg-[#EAB308]/10 border-[#EAB308]/30" :
                    "bg-[#F8F9FA] border-[#E2E8F0]"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#0F172A] truncate">{notion}</span>
                    {getStatusIcon(data.status)}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#64748B]">{data.attempts} essai(s)</span>
                    <span className="font-medium text-[#1A365D]">
                      {data.average > 0 ? `${data.average}/20` : "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activity Timeline */}
        <Card className="card-sjh">
          <CardHeader>
            <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Activité récente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tracking.activity_timeline.length > 0 ? (
              <ScrollArea className="h-64">
                <div className="space-y-3">
                  {tracking.activity_timeline.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 hover:bg-[#F8F9FA] rounded">
                      <div className="w-8 h-8 rounded-full bg-[#1A365D]/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#1A365D]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#0F172A]">
                          {activity.details.exercise_type === "dissertation" ? "Dissertation" : "Commentaire"} sur "{activity.details.notion}"
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={activity.details.score >= 14 ? "bg-[#22C55E]" : activity.details.score >= 10 ? "bg-[#EAB308]" : "bg-[#EF4444]"}>
                            {activity.details.score}/20
                          </Badge>
                          <span className="text-xs text-[#64748B]">
                            {new Date(activity.date).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-center py-8 text-[#64748B]">Aucune activité récente</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StudentTrackingPage;
